import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import {
  History, User, Mail, FileText, CheckCircle2, XCircle, RotateCcw,
  ShieldOff, ShieldCheck, KeyRound, UserPlus, CreditCard,
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EVENT_ICON = {
  profile_updated:             { Icon: User,        cls: 'text-blue-400' },
  email_change_requested:      { Icon: Mail,        cls: 'text-amber-400' },
  email_change_approved:       { Icon: Mail,        cls: 'text-green-400' },
  email_change_rejected:       { Icon: Mail,        cls: 'text-red-400' },
  document_uploaded:           { Icon: FileText,    cls: 'text-blue-400' },
  document_approved:           { Icon: CheckCircle2,cls: 'text-green-400' },
  document_rejected:           { Icon: XCircle,     cls: 'text-red-400' },
  document_reupload_requested: { Icon: RotateCcw,   cls: 'text-amber-400' },
  status_changed:              { Icon: ShieldOff,   cls: 'text-amber-400' },
  password_reset:              { Icon: KeyRound,    cls: 'text-amber-400' },
  invite_sent:                 { Icon: UserPlus,    cls: 'text-blue-400' },
  account_created:             { Icon: ShieldCheck, cls: 'text-green-400' },
  bank_details:                { Icon: CreditCard,  cls: 'text-blue-400' },
};

function fmtDt(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return iso; }
}

export default function EmployeeAuditTimeline({ employeeId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/employees/${employeeId}/audit-timeline`);
      setRows(res.data || []);
    } catch { toast.error('Failed to load audit timeline'); }
    finally { setLoading(false); }
  }, [employeeId]);
  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="py-10 text-center text-muted-foreground animate-pulse">Loading audit timeline…</div>;

  return (
    <Card className="bg-card border-border rounded-sm" data-testid="audit-timeline">
      <CardHeader className="border-b border-border pb-4">
        <CardTitle className="text-sm font-barlow tracking-wider uppercase flex items-center gap-2">
          <History className="w-4 h-4 text-primary" /> Audit Timeline ({rows.length})
        </CardTitle>
      </CardHeader>
      <CardContent className="p-4">
        {rows.length === 0 ? (
          <p className="text-sm text-muted-foreground italic text-center py-6">No events recorded yet.</p>
        ) : (
          <ol className="relative border-l-2 border-border pl-6 space-y-4">
            {rows.map((r) => {
              const ic = EVENT_ICON[r.event] || { Icon: History, cls: 'text-muted-foreground' };
              const I = ic.Icon;
              return (
                <li key={r.id} className="relative" data-testid={`audit-row-${r.event}`}>
                  <span className="absolute -left-[35px] top-0 bg-card border-2 border-border rounded-full w-6 h-6 flex items-center justify-center">
                    <I className={`w-3 h-3 ${ic.cls}`} />
                  </span>
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-sm font-medium">{r.event_label}</p>
                    <Badge className="text-[10px] rounded-sm border bg-muted/30 text-muted-foreground border-border uppercase tracking-wider">{r.changed_by_role || 'system'}</Badge>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {fmtDt(r.changed_at)}{r.changed_by_name && <> · by <span className="text-foreground">{r.changed_by_name}</span></>}
                  </p>
                  {r.event === 'profile_updated' && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Field: <span className="text-foreground">{r.field}</span>
                      {r.old_value && <> · was <s>{r.old_value}</s> · now <span className="text-foreground">{r.new_value}</span></>}
                    </p>
                  )}
                  {r.comment && <p className="text-xs text-muted-foreground mt-1">{r.comment}</p>}
                </li>
              );
            })}
          </ol>
        )}
      </CardContent>
    </Card>
  );
}
